import { buildApiUrl } from "../config/api";
import type { VersionResponse } from "./updateController";

const VERSION_CHECK_TIMEOUT_MS = 10_000;

/**
 * Fetches the current deployment version without using browser or proxy caches.
 *
 * @param fetchImpl - Fetch implementation used for the request.
 * @param now - Timestamp provider used to construct the cache-busting query.
 * @returns The version payload returned by the add-in server.
 * @throws Error when the server response is unsuccessful, cannot be decoded as JSON,
 * or does not settle within the version-check timeout.
 */
export async function fetchCurrentVersion(
  fetchImpl: typeof fetch = fetch,
  now: () => number = Date.now
): Promise<VersionResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS);
  try {
    const response = await fetchImpl(buildApiUrl(`/api/version?_cb=${now()}`), {
      cache: "no-store",
      credentials: "include",
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Version check failed with status ${response.status}`);
    }
    return (await response.json()) as VersionResponse;
  } finally {
    clearTimeout(timeout);
  }
}
